import type {
  AddForecastRequest,
  BomResponse,
  CompletionPlanResponse,
  ConsumptionVoucherResponse,
  CreateBomRequest,
  CreateWorkOrderRequest,
  IssueType,
  PostVoucherRequest,
  ProductionPlanResponse,
  RunPlanRequest,
  UpdateBomRequest,
  UsageRowResponse,
  VarianceCardResponse,
  WhereUsedRow,
  WorkOrderResponse,
} from '@palaccount/contracts';
import {
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE,
  MAX_PICKER_IDS,
  PICKER_PAGE_SIZE,
  pickerIds,
} from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

/**
 * PART 9's data layer — thin TanStack Query wrappers over the typed contract
 * client, the inventory feature's pattern: every non-2xx becomes an ApiError
 * carrying the SERVER's fixed sentence, so the screens never invent wording
 * for a refusal the spec has already worded.
 *
 * Every write invalidates broadly, and that is deliberate rather than lazy:
 * these four screens read each other's data. Logging output changes the
 * consumption ledger (backflush), the ledger changes the work order's actual
 * material, and completing a job changes stock levels the plan nets against.
 */

export const BOMS_KEY = ['manufacturing', 'boms'] as const;
export const WORK_ORDERS_KEY = ['manufacturing', 'work-orders'] as const;
export const CONSUMPTION_KEY = ['manufacturing', 'consumption'] as const;
export const PLAN_KEY = ['manufacturing', 'plan'] as const;

function useInvalidateAll() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['manufacturing'] });
}

// ── MFG-01 ──────────────────────────────────────────────────────────────────

/**
 * MFG-01's OPENING RECIPE, and the whole of what the screen needed the list
 * for — one row, asked for as one row.
 *
 * PAGINATION.md §5a carried MFG-01 as "UNBLOCKED, screen not yet paged", and
 * reading the screen rather than the entry turned out to matter: MFG-01 does
 * not DRAW a recipe list at all. It is an item-keyed master/detail, and the
 * only thing it did with 500 recipes was take `boms[0]` as the default
 * selection. Every other one was decoded, and each carried its components and
 * its routing, because `toResponse` reads both per row.
 *
 * So the conversion here is not a `<Pager>` — a pager over a list nobody
 * renders would be furniture. It is `pageSize: 1`, which is the same answer
 * (the server's ORDER BY decides "first", not the client's) at 1/500th of the
 * response. §3 step 10 in the other direction: the screen derived ONE thing
 * from the whole list, so it asks for one thing.
 */
export function useFirstBom() {
  return useQuery({
    queryKey: [...BOMS_KEY, 'first'],
    queryFn: async (): Promise<BomResponse | null> => {
      const response = await api.boms.list({ query: { page: 1, pageSize: 1 } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.boms[0] ?? null;
    },
  });
}

/** MFG-01's recipe list, one page at a time (NFR-42). */
export function useBoms(finishedItemId?: string, page = 1, pageSize = DEFAULT_PAGE_SIZE) {
  return useQuery({
    queryKey: [...BOMS_KEY, { finishedItemId }, page, pageSize],
    queryFn: async () => {
      const response = await api.boms.list({
        query: { ...(finishedItemId === undefined ? {} : { finishedItemId }), page, pageSize },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * "Does this item already have a live recipe?" — asked of the SERVER, for one
 * item, at the moment somebody picks it.
 *
 * MFG-01 answered this by searching the recipe array it had already loaded,
 * which is why PAGINATION.md §5a would not let the screen page: a recipe past
 * the cut reads as "no recipe" and §15 then opens a SECOND Draft v1 for an
 * item that already has one. One question about one item belongs in one
 * query, and then neither the list nor the picker has to be complete.
 *
 * `MAX_PAGE_SIZE` here is not the unbounded read `UNPAGED_CALLERS` is about:
 * `finishedItemId` narrows this to ONE item's version history, which the
 * version counter bounds at a handful. It reads them all because the answer
 * is "the newest version that is not inactive", and Draft and Active are two
 * statuses — asking for one of them would answer a different question.
 */
export async function findLiveBom(finishedItemId: string): Promise<BomResponse | null> {
  const response = await api.boms.list({
    query: { finishedItemId, page: 1, pageSize: MAX_PAGE_SIZE },
  });
  if (response.status !== 200) throwApiError(response.status, response.body);
  return response.body.boms.find((row) => row.status !== 'inactive') ?? null;
}

export function useBom(bomId: string | undefined) {
  return useQuery({
    queryKey: [...BOMS_KEY, bomId],
    enabled: bomId !== undefined,
    queryFn: async (): Promise<BomResponse> => {
      const response = await api.boms.get({ params: { bomId: bomId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useWhereUsed(itemId: string | null) {
  return useQuery({
    queryKey: [...BOMS_KEY, 'where-used', itemId],
    enabled: itemId !== null,
    queryFn: async (): Promise<WhereUsedRow[]> => {
      const response = await api.boms.whereUsed({ params: { itemId: itemId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.rows;
    },
  });
}

export function useBomMutations() {
  const invalidate = useInvalidateAll();
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: async (body: CreateBomRequest): Promise<BomResponse> => {
      const response = await api.boms.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    /**
     * ── ONE RECIPE WRITE AT A TIME, and this is a fix for a LOST UPDATE ──────
     *
     * `PATCH /boms/:id` REPLACES the whole component list, and MFG-01's editor
     * fires one from every `onBlur` without awaiting it. Tab quickly from a
     * quantity to a scrap box to the next row's scrap box and three whole-list
     * writes are in flight at once — and the server cannot put them back in
     * order, because nothing on the wire says which was typed last. Each
     * request does its own `SELECT`, then `UPDATE bom_versions`, and it is
     * THAT statement that takes the row lock: whichever request reaches it
     * first commits first, and under load that is not the order they arrived
     * in. The last thing the user typed is then silently overwritten by an
     * earlier one.
     *
     * MEASURED, not deduced: three overlapping writes setting a component's
     * scrap to 0, then 3, then 5, over 60 rounds through the real HTTP stack —
     * **43 of 60 ended on the wrong value** (17 × the correct 4.200, 17 ×
     * 4.120 from the second write, 26 × 4.000 from the FIRST). `4.000` where
     * `4.200` was expected is exactly what `manufacturing.spec.ts` reported
     * once and could not explain: a scrap allowance vanishing is a wrong
     * number, not a timing artefact, and the journey only usually escapes it
     * because Playwright's own per-action overhead keeps the writes apart.
     *
     * `scope` is react-query's own serialisation: mutations sharing a scope id
     * run one after another instead of together. It belongs HERE rather than
     * in the screen because every section of MFG-01 — the header, the
     * component list, the routing and the overhead box — writes the SAME
     * document through this one mutation, so a per-list queue would still race
     * across sections.
     *
     * The screen's local draft already makes each payload carry every earlier
     * edit (see `ComponentList`), so serialising loses nothing: the last
     * request sent is the fullest one, and now it is also the last applied.
     *
     * ── ⚠ IF YOU ADD `scope` TO A MUTATION, READ THIS ───────────────────────
     *
     * `scope` SERIALISES THE WHOLE MUTATION LIFECYCLE, NOT JUST THE REQUEST —
     * and `onSuccess` is inside it. React Query awaits whatever `onSuccess`
     * returns before the next scoped mutation may start, and
     * `invalidateQueries` returns a promise that resolves only when every
     * refetch it triggered has settled. So `scope` + `onSuccess: invalidate`
     * quietly puts every READ into the write queue, and each edit then waits
     * on the previous edit's REFETCHES. "We serialised the writes" is the
     * sentence that gets repeated; this is what it drags in behind it.
     *
     * That is not a hypothetical — it is what this mutation did for one
     * commit, and it made MFG-01 worse than the race it was fixing.
     *
     * ── WHY THE QUEUE ALONE WAS NOT ENOUGH, and what it cost ────────────────
     *
     * Serialising the writes and leaving `onSuccess: invalidate` put the READS
     * inside the queue. React Query waits for the promise `onSuccess` returns
     * before a scoped mutation is finished, and `invalidateQueries` resolves
     * only when every refetch it triggered has settled — so each queued edit
     * waited for the previous edit's WRITE **and its refetches**. Measured on a
     * fake server with 1,200 ms reads, four edits: the four writes left at
     * 3,960 / 5,477 / 6,687 / 7,890 ms — gaps of ~1,200 ms, i.e. exactly one
     * read apiece. The screen is correct at the end and wrong in the middle for
     * as long as the chain takes, and the journey's own 15 s budget is not a
     * generous allowance when a loaded four-worker suite makes a BOM read take
     * seconds and `['manufacturing']` refetches TEN queries per write.
     *
     * That is why `4.000` survived the first fix: it is the state after the
     * QUANTITY write, sitting on screen while three scrap writes queue behind
     * refetches nobody is waiting for.
     *
     * So the queue keeps only the WRITES in it:
     *
     *  - `onSuccess` seeds this document's own cache entry from the response
     *    it already has. The response IS the post-write state, so a read cannot
     *    tell it anything a write did not, and no read can arrive late and
     *    rewind the screen to a state before an edit that already committed.
     *  - the broad invalidation still happens — these four screens read each
     *    other's data — but it is FIRED AND NOT AWAITED, so it never sits
     *    between one edit and the next.
     *
     * ── WHAT THIS DOES NOT FIX, and what fixing it would take ──────────────
     *
     * Two PEOPLE editing one draft recipe at the same time still lose one
     * another's edits, silently and in the database, for the original reason:
     * the route replaces the whole component list and nothing tells it which
     * list is newer. Nothing here can help, because both browsers are behaving
     * correctly and neither knows the other exists.
     *
     * What it would take, concretely:
     *
     *  1. `bom_versions.updated_at` (it already exists) goes out on
     *     `BomResponse` — it already does — and comes BACK on
     *     `UpdateBomRequest` as the version the editor believes it is changing;
     *  2. `BomService.update` compares it under the row lock it already takes
     *     and answers 409 with a sentence of its own when it does not match —
     *     the shape `CREDIT_MESSAGES`/`MFG_MESSAGES` refusals already use;
     *  3. MFG-01 shows that refusal and offers to reload, because a 409 the
     *     screen swallows is worse than the lost update it replaced.
     *
     * It is a contract change plus a refusal a screen must be able to draw, so
     * it is a slice rather than a line, and it is NOT what the journey caught.
     * This removes the concurrency a SINGLE editor creates, which is all of
     * the observed defect and none of that one.
     */
    scope: { id: 'bom-update' },
    mutationFn: async ({ bomId, body }: { bomId: string; body: UpdateBomRequest }) => {
      const response = await api.boms.update({ params: { bomId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: (bom: BomResponse) => {
      // The written document, straight into its own cache entry — no read.
      queryClient.setQueryData([...BOMS_KEY, bom.id], bom);
      // Everything else this write touches, refreshed but NOT awaited: the
      // `void` is what keeps the refetches out of the serialised queue.
      void invalidate();
    },
  });
  /**
   * §8.5's activate prompt has a server half: without `deactivateIncumbent`
   * the API refuses to retire a live recipe, so the dialog cannot be skipped
   * by a caller that simply does not draw it.
   */
  const setStatus = useMutation({
    mutationFn: async ({
      bomId,
      status,
      deactivateIncumbent,
    }: {
      bomId: string;
      status: 'draft' | 'active' | 'inactive';
      deactivateIncumbent?: boolean;
    }) => {
      const response = await api.boms.setStatus({
        params: { bomId },
        body: { status, ...(deactivateIncumbent === undefined ? {} : { deactivateIncumbent }) },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (bomId: string) => {
      const response = await api.boms.remove({ params: { bomId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: invalidate,
  });
  return { create, update, setStatus, remove };
}

// ── MFG-02 ──────────────────────────────────────────────────────────────────

/** MFG-02's board, one page at a time (NFR-42). */
export function useWorkOrders(page = 1, pageSize = DEFAULT_PAGE_SIZE) {
  return useQuery({
    queryKey: [...WORK_ORDERS_KEY, page, pageSize],
    queryFn: async () => {
      const response = await api.workOrders.list({ query: { page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * THE WORK-ORDER PICKER'S two reads (GEN-50) — MFG-04's ledger filter and its
 * voucher form, which PAGINATION.md §5a carried for four waves as "left, and
 * now a straight conversion".
 *
 * It used to be one read: `useWorkOrdersForPicker()`, asking for
 * `MAX_PAGE_SIZE` — a silent cut at 500 jobs, which is a year of production
 * for a busy shop. Two bounded reads replace it.
 */
export function useWorkOrderSearch(q: string) {
  return useQuery({
    queryKey: [...WORK_ORDERS_KEY, 'search', q],
    queryFn: async (): Promise<WorkOrderResponse[]> => {
      const response = await api.workOrders.list({
        query: { ...(q === '' ? {} : { q }), pageSize: PICKER_PAGE_SIZE },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.workOrders;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * The jobs a form already holds. Not narrowed by status: a CORRECTION voucher
 * names the job it corrects, and that job is routinely closed by the time
 * somebody opens the correction (BR-MFG-04-20's "the two that need
 * explaining" are written late, not early).
 */
export function useWorkOrdersHeld(ids: readonly string[]) {
  const key = [...new Set(ids)].sort();
  return useQuery({
    queryKey: [...WORK_ORDERS_KEY, 'held', key],
    enabled: key.length > 0,
    queryFn: async (): Promise<WorkOrderResponse[]> => {
      const response = await api.workOrders.list({
        query: { ids: pickerIds(key), pageSize: MAX_PICKER_IDS },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.workOrders;
    },
  });
}

export function useWorkOrder(workOrderId: string | undefined) {
  return useQuery({
    queryKey: [...WORK_ORDERS_KEY, workOrderId],
    enabled: workOrderId !== undefined,
    queryFn: async (): Promise<WorkOrderResponse> => {
      const response = await api.workOrders.get({ params: { workOrderId: workOrderId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/**
 * §9's quick-complete convenience, and the whole of it: a READ that returns
 * what a one-gesture completion would propose. The screen shows the figures,
 * lets them be changed, and then calls the same four writes below.
 */
export function useCompletionPlan(workOrderId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...WORK_ORDERS_KEY, workOrderId, 'completion-plan'],
    enabled: workOrderId !== undefined && enabled,
    queryFn: async (): Promise<CompletionPlanResponse> => {
      const response = await api.workOrders.completionPlan({
        params: { workOrderId: workOrderId! },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useWorkOrderMutations() {
  const invalidate = useInvalidateAll();

  const create = useMutation({
    mutationFn: async (body: CreateWorkOrderRequest): Promise<WorkOrderResponse> => {
      const response = await api.workOrders.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });

  const reserve = useMutation({
    mutationFn: async (workOrderId: string) => {
      const response = await api.workOrders.reserve({ params: { workOrderId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });

  const unreserve = useMutation({
    mutationFn: async (workOrderId: string) => {
      const response = await api.workOrders.unreserve({
        params: { workOrderId } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });

  /**
   * BR-MFG-02-07: without `acknowledgeShortage` a short job is REFUSED, and
   * the refusal carries §8.5's own sentence — which is what the shortage modal
   * shows. The check lives on the server, so the modal is how the answer is
   * collected rather than where the rule lives.
   */
  const start = useMutation({
    mutationFn: async ({
      workOrderId,
      acknowledgeShortage,
    }: {
      workOrderId: string;
      acknowledgeShortage?: boolean;
    }) => {
      const response = await api.workOrders.start({
        params: { workOrderId },
        body: acknowledgeShortage === undefined ? {} : { acknowledgeShortage },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });

  const logOutput = useMutation({
    mutationFn: async ({
      workOrderId,
      goodQty,
      scrapQty,
      entryMode,
    }: {
      workOrderId: string;
      goodQty: string;
      scrapQty: string;
      entryMode?: 'typed' | 'prefilled';
    }) => {
      const response = await api.workOrders.logOutput({
        params: { workOrderId },
        body: { goodQty, scrapQty, ...(entryMode === undefined ? {} : { entryMode }) },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });

  const reverseOutput = useMutation({
    mutationFn: async ({ workOrderId, logId }: { workOrderId: string; logId: string }) => {
      const response = await api.workOrders.reverseOutput({
        params: { workOrderId, logId } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });

  const complete = useMutation({
    mutationFn: async (workOrderId: string) => {
      const response = await api.workOrders.complete({ params: { workOrderId } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });

  const close = useMutation({
    mutationFn: async (workOrderId: string) => {
      const response = await api.workOrders.close({ params: { workOrderId } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });

  const cancel = useMutation({
    mutationFn: async (workOrderId: string) => {
      const response = await api.workOrders.cancel({ params: { workOrderId } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });

  const hold = useMutation({
    mutationFn: async ({ workOrderId, reason }: { workOrderId: string; reason: string }) => {
      const response = await api.workOrders.hold({ params: { workOrderId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });

  const resume = useMutation({
    mutationFn: async (workOrderId: string) => {
      const response = await api.workOrders.resume({ params: { workOrderId } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });

  const updateOperation = useMutation({
    mutationFn: async ({
      workOrderId,
      operationId,
      body,
    }: {
      workOrderId: string;
      operationId: string;
      body: { status?: 'not_started' | 'in_progress' | 'done'; actualMinutes?: number; operator?: string | null };
    }) => {
      const response = await api.workOrders.updateOperation({
        params: { workOrderId, operationId },
        body,
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });

  return {
    create,
    reserve,
    unreserve,
    start,
    logOutput,
    reverseOutput,
    complete,
    close,
    cancel,
    hold,
    resume,
    updateOperation,
  };
}

// ── MFG-04 ──────────────────────────────────────────────────────────────────

export interface LedgerFilters {
  workOrderId?: string;
  itemId?: string;
  issueType?: IssueType;
  warehouseId?: string;
  from?: string;
  to?: string;
}

/**
 * MFG-04's ledger, one page at a time (NFR-42). Every filter already ran in
 * SQL; `totalValue` (BR-MFG-04-14) is an aggregate over the whole FILTERED
 * set, so paging narrows the rows and never the figure above them.
 */
export function useConsumptionLedger(
  filters: LedgerFilters,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...CONSUMPTION_KEY, filters, page, pageSize],
    queryFn: async () => {
      const response = await api.materialConsumption.ledger({
        query: { ...filters, page, pageSize },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useConsumptionVariance() {
  return useQuery({
    queryKey: [...CONSUMPTION_KEY, 'variance'],
    queryFn: async (): Promise<VarianceCardResponse[]> => {
      const response = await api.materialConsumption.variance({ query: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.cards;
    },
  });
}

export function useUsageReport(from: string, to: string) {
  return useQuery({
    queryKey: [...CONSUMPTION_KEY, 'usage', from, to],
    queryFn: async (): Promise<{ rows: UsageRowResponse[]; totalValue: string }> => {
      const response = await api.materialConsumption.usage({ query: { from, to } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/** ONE form, two doors (§5.5) — `correctsVoucherId` is what makes it the second. */
export function usePostVoucher() {
  const invalidate = useInvalidateAll();
  return useMutation({
    mutationFn: async (body: PostVoucherRequest): Promise<ConsumptionVoucherResponse> => {
      const response = await api.materialConsumption.post({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

// ── MFG-03 ──────────────────────────────────────────────────────────────────

export function useProductionPlan() {
  return useQuery({
    queryKey: PLAN_KEY,
    queryFn: async (): Promise<ProductionPlanResponse> => {
      const response = await api.productionPlanning.plan();
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

/*
 * MFG-03's FOUR LISTS, each its own paged read (NFR-42).
 *
 * They used to be four arrays inside the plan above, which at PART 20.7's
 * volumes made one screen 10.5 MB — and 7.4 MB of that was the net-requirement
 * table sent twice, once as itself and once as BR-MFG-03-07's requisition
 * band. The band is the SAME hook with `shortOnly`, so the rule's "a filtered
 * view of the net-requirements table, never a separate calculation" is now
 * literally one route with one predicate.
 */

export function usePlanDemand(page: number, pageSize = DEFAULT_PAGE_SIZE) {
  return useQuery({
    queryKey: [...PLAN_KEY, 'demand', page, pageSize],
    queryFn: async () => {
      const response = await api.productionPlanning.demand({ query: { page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/** §5.6's table when `shortOnly` is false; §5.7's band when it is true. */
export function usePlanNetRequirements(
  page: number,
  shortOnly: boolean,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...PLAN_KEY, 'net-requirements', { shortOnly }, page, pageSize],
    queryFn: async () => {
      const response = await api.productionPlanning.netRequirements({
        query: { page, pageSize, ...(shortOnly ? { shortOnly: 'true' as const } : {}) },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function usePlanReorderTargets(page: number, pageSize = DEFAULT_PAGE_SIZE) {
  return useQuery({
    queryKey: [...PLAN_KEY, 'reorder-targets', page, pageSize],
    queryFn: async () => {
      const response = await api.productionPlanning.reorderTargets({ query: { page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function usePlannedOrders(page: number, pageSize = DEFAULT_PAGE_SIZE) {
  return useQuery({
    queryKey: [...PLAN_KEY, 'planned-orders', page, pageSize],
    queryFn: async () => {
      const response = await api.productionPlanning.plannedOrders({ query: { page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function usePlanningMutations() {
  const invalidate = useInvalidateAll();
  const run = useMutation({
    mutationFn: async (body: RunPlanRequest): Promise<ProductionPlanResponse> => {
      const response = await api.productionPlanning.run({ body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const addForecast = useMutation({
    mutationFn: async (body: AddForecastRequest) => {
      const response = await api.productionPlanning.addForecast({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const createFromDemand = useMutation({
    mutationFn: async (body: {
      itemId: string;
      qty: string;
      dueDate: string;
      warehouseId: string;
    }): Promise<WorkOrderResponse> => {
      const response = await api.productionPlanning.createFromDemand({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const convert = useMutation({
    mutationFn: async ({
      plannedOrderId,
      warehouseId,
    }: {
      plannedOrderId: string;
      warehouseId: string;
    }): Promise<WorkOrderResponse> => {
      const response = await api.productionPlanning.convert({
        params: { plannedOrderId },
        body: { warehouseId },
      });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  return { run, addForecast, createFromDemand, convert };
}
